// src/utils/currency.js
/**
 * Universal Indian Currency & Number Formatter for No Brokerage Admin Panel.
 * Formats numbers into standard Indian numbering system (Lakhs & Crores, e.g. ₹9,11,411).
 */

/**
 * Format an amount into Indian Currency (e.g., 911411 -> "₹9,11,411")
 * 
 * @param {number|string} amount - The numeric or string value to format
 * @param {object} [options]
 * @param {string} [options.fallback='₹0'] - Value returned when amount is null/undefined/invalid
 * @param {number} [options.maximumFractionDigits=0] - Number of decimal places
 * @param {boolean} [options.showSymbol=true] - Whether to prepend ₹ symbol
 * @param {boolean} [options.compact=false] - Whether to format compactly (e.g. ₹1.5 Cr, ₹50 L)
 * @returns {string} Formatted currency string
 */
export const formatIndianCurrency = (amount, options = {}) => {
  const {
    fallback = '₹0',
    maximumFractionDigits = 0,
    showSymbol = true,
    compact = false
  } = options;

  if (amount === null || amount === undefined || amount === '') {
    return fallback;
  }

  // Handle strings (clean existing $ or ₹ or commas)
  if (typeof amount === 'string') {
    const trimmed = amount.trim();
    if (trimmed.startsWith('$') || trimmed.startsWith('₹')) {
      const numericPart = trimmed.replace(/[$,₹,\s]/g, '');
      if (!isNaN(numericPart) && numericPart !== '') {
        amount = Number(numericPart);
      } else {
        // Descriptive string like "$50k - $1L" -> replace $ with ₹
        return trimmed.replace(/\$/g, '₹');
      }
    } else if (!isNaN(trimmed) && trimmed !== '') {
      amount = Number(trimmed);
    } else {
      // If it contains descriptive words like "50 Lakhs" or "Negotiable", return with $ replaced
      return trimmed.replace(/\$/g, '₹');
    }
  }

  const num = Number(amount);
  if (isNaN(num)) {
    return fallback;
  }

  // Compact Indian notation for large numbers if requested
  if (compact) {
    const symbol = showSymbol ? '₹' : '';
    const abs = Math.abs(num);
    if (abs >= 10000000) {
      // Crores
      const cr = (num / 10000000).toFixed(2).replace(/\.00$/, '');
      return `${symbol}${cr} Cr`;
    }
    if (abs >= 100000) {
      // Lakhs
      const lk = (num / 100000).toFixed(2).replace(/\.00$/, '');
      return `${symbol}${lk} L`;
    }
    if (abs >= 1000) {
      // Thousands
      const k = (num / 1000).toFixed(1).replace(/\.0$/, '');
      return `${symbol}${k} K`;
    }
  }

  // Standard Indian formatting (e.g., 9,11,411)
  if (!showSymbol) {
    return new Intl.NumberFormat('en-IN', {
      maximumFractionDigits
    }).format(num);
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits
  }).format(num);
};

/**
 * Format a number in Indian comma format without the currency symbol (e.g., 911411 -> "9,11,411")
 */
export const formatIndianNumber = (amount, maximumFractionDigits = 0) => {
  return formatIndianCurrency(amount, {
    showSymbol: false,
    maximumFractionDigits,
    fallback: '0'
  });
};

export default formatIndianCurrency;

